import { BLOCK_RENDER_DISTANCE } from '../world/constants';
import {
  animationsEqual,
  DEFAULT_ANIMATION_FLAGS,
  type AnimationFlags,
} from '../world/animationControl';

export type { AnimationFlags, AnimationKind } from '../world/animationControl';
export { ANIMATION_KINDS, DEFAULT_ANIMATION_FLAGS } from '../world/animationControl';

/** Live toggles for the in-game developer render panel. */
export interface RenderSettingsState {
  shadows: boolean;
  /** Directional shadow map width/height in pixels. */
  shadowResolution: number;
  glow: boolean;
  animations: AnimationFlags;
  /** Horizontal block radius for terrain / chunk visibility. */
  renderDistance: number;
}

export type RenderSettingsListener = (state: Readonly<RenderSettingsState>) => void;

export const RENDER_DISTANCE_MIN = 32;
export const RENDER_DISTANCE_MAX = 256;
export const RENDER_DISTANCE_STEP = 16;

/** Discrete shadow-map sizes exposed in the Dev panel. */
export const SHADOW_RESOLUTIONS = [1024, 2048, 4096, 8192] as const;
export const DEFAULT_SHADOW_RESOLUTION = 4096;

export function clampRenderDistance(blocks: number): number {
  const stepped = Math.round(blocks / RENDER_DISTANCE_STEP) * RENDER_DISTANCE_STEP;
  return Math.min(RENDER_DISTANCE_MAX, Math.max(RENDER_DISTANCE_MIN, stepped));
}

export function clampShadowResolution(value: number): number {
  let best: number = SHADOW_RESOLUTIONS[0];
  let bestDist = Math.abs(value - best);
  for (const candidate of SHADOW_RESOLUTIONS) {
    const dist = Math.abs(value - candidate);
    if (dist < bestDist) {
      best = candidate;
      bestDist = dist;
    }
  }
  return best;
}

export class RenderSettings {
  private state: RenderSettingsState = {
    shadows: true,
    shadowResolution: DEFAULT_SHADOW_RESOLUTION,
    glow: true,
    animations: { ...DEFAULT_ANIMATION_FLAGS },
    renderDistance: BLOCK_RENDER_DISTANCE,
  };

  private readonly listeners = new Set<RenderSettingsListener>();

  get(): Readonly<RenderSettingsState> {
    return this.state;
  }

  patch(partial: Partial<RenderSettingsState>): void {
    const next: RenderSettingsState = {
      ...this.state,
      ...partial,
      animations: partial.animations
        ? { ...this.state.animations, ...partial.animations }
        : this.state.animations,
    };
    if (partial.renderDistance !== undefined) {
      next.renderDistance = clampRenderDistance(partial.renderDistance);
    }
    if (partial.shadowResolution !== undefined) {
      next.shadowResolution = clampShadowResolution(partial.shadowResolution);
    }
    const changed =
      next.shadows !== this.state.shadows ||
      next.shadowResolution !== this.state.shadowResolution ||
      next.glow !== this.state.glow ||
      !animationsEqual(next.animations, this.state.animations) ||
      next.renderDistance !== this.state.renderDistance;
    if (!changed) return;
    this.state = next;
    for (const listener of this.listeners) listener(this.state);
  }

  subscribe(listener: RenderSettingsListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }
}
